import { useAuth } from '../../contexts/AuthContext';
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Calendar, Download, Loader2 } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currentYr = new Date().getFullYear();
const YEARS = Array.from({ length: Math.max(1, currentYr - 2025 + 1) }, (_, i) => String(2025 + i));

const StrengthReportView = ({
  initialYear = String(new Date().getFullYear()),
  initialMonth = String(new Date().getMonth() + 1),
  initialDepartment = 'ALL',
  departmentsList = [],
}) => {
  const { hasPermission } = useAuth();
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [department, setDepartment] = useState(initialDepartment);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const departmentOptions = useMemo(() => {
    const names = departmentsList.map((dept) => dept.name).filter(Boolean);
    return ['ALL', ...Array.from(new Set(names)).sort()];
  }, [departmentsList]);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ year, month });
        if (department && department !== 'ALL') {
          params.set('department', department);
        }
        const response = await fetch(buildApiUrl(`/api/v1/reports/strength-report?${params}`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Failed to load Strength report');
        setData(await response.json());
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    if (/^\d{4}$/.test(year) && month) load();
    return () => controller.abort();
  }, [year, month, department]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ year, month });
      if (department && department !== 'ALL') {
        params.set('department', department);
      }
      const monthLabel = MONTHS.find((item) => item.value === month)?.label || month;
      await downloadAuthenticatedFile(
        `/api/v1/reports/strength-report-excel?${params}`,
        `Strength_Report_${monthLabel}_${year}.xlsx`,
      );
    } catch (err) {
      setError(err.message || 'Failed to download Strength Excel report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="print:hidden flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <Calendar className="mb-2 h-5 w-5 text-rose-600" />
        <label className="text-xs font-semibold text-gray-500">
          YEAR
          <select
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className="mt-1 block w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-gray-800"
          >
            {YEARS.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-500">
          MONTH
          <select
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="mt-1 block w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-gray-800"
          >
            {MONTHS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-500">
          DEPARTMENT
          <select
            value={department}
            onChange={(event) => setDepartment(event.target.value)}
            className="mt-1 block min-w-[180px] rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-gray-800"
          >
            {departmentOptions.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>
        {hasPermission('report:export') && (
          <button
            onClick={downloadExcel}
            disabled={downloading || loading}
            className="ml-auto flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download Excel
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border-2 border-gray-700 bg-white p-5 shadow-lg">
        <div className="mb-4 font-serif">
          <h3 className="text-base font-bold uppercase">{data?.month_label || 'STRENGTH REPORT'}</h3>
        </div>
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-rose-600" />
          </div>
        ) : (
          <table className="w-full min-w-[1100px] border-collapse font-mono text-sm text-black">
            <thead>
              <tr className="border-b-2 border-gray-900 font-bold">
                <th className="border border-gray-400 px-3 py-2 text-left">Code No.</th>
                <th className="border border-gray-400 px-3 py-2 text-left">Name</th>
                <th className="border border-gray-400 px-3 py-2 text-left">Surname</th>
                <th className="border border-gray-400 px-3 py-2 text-left">Designation</th>
                <th className="border border-gray-400 px-3 py-2 text-left">Head Code</th>
                <th className="border border-gray-400 px-3 py-2 text-left">Dept</th>
                <th className="border border-gray-400 px-3 py-2 text-left">Account No.</th>
                <th className="border border-gray-400 px-3 py-2 text-left">Bank Name</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((row, index) => (
                <tr key={`${row.code_no}-${index}`}>
                  <td className="border border-gray-400 px-3 py-1.5">{row.code_no}</td>
                  <td className="border border-gray-400 px-3 py-1.5 uppercase">{row.first_name}</td>
                  <td className="border border-gray-400 px-3 py-1.5 uppercase">{row.last_name}</td>
                  <td className="border border-gray-400 px-3 py-1.5">{row.designation}</td>
                  <td className="border border-gray-400 px-3 py-1.5">{row.head_code}</td>
                  <td className="border border-gray-400 px-3 py-1.5">{row.dept}</td>
                  <td className="border border-gray-400 px-3 py-1.5">{row.account_no}</td>
                  <td className="border border-gray-400 px-3 py-1.5">{row.bank_name}</td>
                </tr>
              ))}
              {!loading && (data?.rows || []).length === 0 && (
                <tr>
                  <td colSpan={8} className="border border-gray-400 px-3 py-8 text-center text-gray-500">
                    No strength records found for the selected period.
                  </td>
                </tr>
              )}
              {!loading && (data?.rows || []).length > 0 && (
                <tr className="bg-gray-100 font-bold">
                  <td className="border border-gray-400 px-3 py-2" colSpan={7}>Total</td>
                  <td className="border border-gray-400 px-3 py-2 text-right">{data?.total || 0}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StrengthReportView;
